import { useState } from 'react';
import { useEagerAndDebouncedState } from 'src/hooks/useEagerDebouncedState';
import {
  createWorkflowRunFiltersApi,
  PAGE_SIZE_OPTIONS,
} from './createWorkflowRunFiltersApi';
import {
  DEFAULT_RUN_LIST_SORT,
  type RunListSort,
  type RunStatusOption,
  type TriggerSourceFilter,
} from './workflowRunQuery';

export {
  PAGE_SIZE_OPTIONS,
  type PageSizeOption,
} from './createWorkflowRunFiltersApi';

export {
  RUN_STATUS_FILTER_OPTIONS,
  RUN_STATUS_OPTIONS,
  isRunStatusOption,
  type RunListSortDirection,
  type RunStatusFilter,
  type RunStatusOption,
  type TriggerSourceFilter,
} from './workflowRunQuery';

export type { WorkflowRunFiltersApi } from './createWorkflowRunFiltersApi';

/**
 * Filter, sort, and pagination state for the workflow run lists.
 *
 * Status is held as a multi-select (`statuses`) for the per-workflow runs
 * tab's column-header filters. The All Runs page still drives it through the
 * single-value `statusFilter` / `onStatusFilterChange` pair, which maps onto
 * the same array (one selected value, or none for "all").
 *
 * Local React state by default. All Runs passes a URL-backed store instead
 * so Back/Forward keeps the same filters.
 */
export function useWorkflowRunFilters() {
  const [statuses, setStatuses] = useState<RunStatusOption[]>([]);
  const [triggerSourceFilter, setTriggerSourceFilter] =
    useState<TriggerSourceFilter>('all');
  const [triggeredFrom, setTriggeredFrom] = useState('');
  const [triggeredTo, setTriggeredTo] = useState('');
  const [testCaseIds, setTestCaseIds] = useState<string[]>([]);
  const [sort, setSort] = useState<RunListSort>(DEFAULT_RUN_LIST_SORT);
  const [pageSize, setPageSize] =
    useState<(typeof PAGE_SIZE_OPTIONS)[number]>(25);
  const [page, setPage] = useState(1);
  const {
    eagerValue: searchTerm,
    debouncedValue: debouncedSearchTerm,
    setValue: setSearchTerm,
  } = useEagerAndDebouncedState('', 300);

  return createWorkflowRunFiltersApi({
    statuses,
    triggerSourceFilter,
    triggeredFrom,
    triggeredTo,
    testCaseIds,
    sort,
    pageSize,
    page,
    searchTerm,
    debouncedSearchTerm,
    setStatuses,
    setTriggerSourceFilter,
    setTriggeredFrom,
    setTriggeredTo,
    setTestCaseIds,
    setSort,
    setPageSize,
    setPage,
    setSearchTerm,
    clearFilters: () => {
      setStatuses([]);
      setTriggerSourceFilter('all');
      setTestCaseIds([]);
      setSearchTerm('');
      setTriggeredFrom('');
      setTriggeredTo('');
      setPage(1);
    },
  });
}
